import React, { useState } from "react";
import { FiCheckSquare, FiChevronDown } from "react-icons/fi";
import { useGetFaqListByPaginationQuery } from "../../Services/FAQService";

const FaqTab = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState<number | null>(
    null,
  );
  const [page, setPage] = useState(1);
  // 10 per page, matching Admin's FAQ list. At the previous 25 the pager was
  // effectively dead: with 17 FAQs totalPages was always 1, so the control
  // (which correctly hides at one page) never appeared and the customer page
  // looked like it had no pagination at all.
  const pageSize = 10;

  const { data: faqResponse, isLoading } = useGetFaqListByPaginationQuery({
    page,
    pageSize,
  });

  const handleAccordionChange = (index: number) => () => {
    setExpandedAccordion(expandedAccordion === index ? null : index);
  };

  const totalPages = faqResponse?.totalPages || 1;
  const faqData = faqResponse?.data || [];

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-6 pb-6 border bg-gray-50 dark:bg-dark-background dark:border-dark-foreground rounded-sm">
        {/* Sidebar */}
        <div className="w-full lg:w-1/3 border-r lg:border-b-0 border-gray-200 dark:border-dark-foreground bg-slate-50 dark:bg-dark-foreground p-4">
          <div className="flex lg:flex-col gap-2 overflow-x-auto">
            {!isLoading &&
              Array.isArray(faqData) &&
              faqData.map((item: any, index: number) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedTab(index);
                    setExpandedAccordion(null);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap m-1 ${
                    selectedTab === index
                      ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-semibold"
                      : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                  }`}
                >
                  <FiCheckSquare />
                  {item.title.toUpperCase()}
                </button>
              ))}
          </div>
          <div className="hidden lg:block mt-6 text-center">
            <img
              src="/imgs/3dHuman.png"
              alt="3D Human"
              className="mx-auto opacity-50 w-2/3"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex flex-col w-full lg:w-3/4 space-y-6 bg-slate-50 dark:bg-dark-background p-4">
          {!isLoading &&
            Array.isArray(faqData) &&
            faqData.map((item: any, index: number) =>
              index === selectedTab ? (
                <div key={index}>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2 dark:text-white">
                      <FiCheckSquare />
                      <h2 className="text-xl font-semibold dark:text-white">
                        {item.title}
                      </h2>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Get help with {item.title.toLowerCase()}
                  </p>

                  {item.faqs.map((faq: any, faqIndex: number) => (
                    <div
                      key={faqIndex}
                      className="border-b border-gray-200 dark:border-dark-foreground"
                    >
                      <button
                        onClick={handleAccordionChange(faqIndex)}
                        className="w-full flex justify-between items-center py-3 text-left focus:outline-none px-4 bg-[rgba(0,0,0,0.03)] dark:bg-dark-foreground dark:text-white"
                      >
                        <span className="text-base font-medium dark:text-white">
                          {faq.question}
                        </span>
                        <FiChevronDown
                          className={`transform transition-transform duration-200 ${
                            expandedAccordion === faqIndex ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {expandedAccordion === faqIndex && (
                        <div className="px-4 pb-4 text-sm text-gray-700 dark:text-gray-300 mt-2">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : null,
            )}
        </div>
      </div>
      <div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 bg-gray-200 dark:bg-dark-foreground dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => {
                  setPage(i + 1);
                  setSelectedTab(0);
                  setExpandedAccordion(null);
                }}
                className={`px-3 py-1 rounded ${
                  page === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 dark:bg-dark-foreground dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className="px-3 py-1 bg-gray-200 dark:bg-dark-foreground dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FaqTab;
